import { Minus, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { clamp } from '@/lib/utils';

export function Section({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex flex-col gap-2.5 px-3.5 py-3.5', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between">
          {title && (
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
              {title}
            </h3>
          )}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Row({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <span className="text-[12.5px] text-[var(--text-secondary)]">{label}</span>
      {children}
    </div>
  );
}

export function Stepper({
  value,
  min = 1,
  max = 12,
  onChange,
  label,
}: {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  label: string;
}) {
  const set = (v: number) => onChange(clamp(v, min, max));
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center rounded-[8px] bg-[var(--bg-sunken)] shadow-[inset_0_0_0_1px_var(--hairline)]">
        <button
          aria-label={`Decrease ${label}`}
          onClick={() => set(value - 1)}
          disabled={value <= min}
          className="flex h-7 w-7 items-center justify-center rounded-l-[8px] text-[var(--text-secondary)] transition hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] disabled:opacity-30"
        >
          <Minus size={13} />
        </button>
        <span className="w-7 text-center font-mono text-[12.5px] tabular-nums">{value}</span>
        <button
          aria-label={`Increase ${label}`}
          onClick={() => set(value + 1)}
          disabled={value >= max}
          className="flex h-7 w-7 items-center justify-center rounded-r-[8px] text-[var(--text-secondary)] transition hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] disabled:opacity-30"
        >
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-panel-solid)]',
        checked ? 'bg-[var(--accent)]' : 'bg-[var(--bg-active)]',
      )}
    >
      {/* `left` must be explicit: a button centres its static position, which
          would otherwise be added on top of the translate. */}
      <span
        className={cn(
          'absolute left-0 top-[2px] h-[18px] w-[18px] rounded-full bg-white',
          'shadow-[0_1px_3px_rgba(0,0,0,0.3)]',
          'transition-transform duration-200 [transition-timing-function:var(--ease)]',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

export function ColorInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={label}
        className="swatch h-7 w-9 rounded-[7px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <input
        aria-label={`${label} hex`}
        value={value.toUpperCase()}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) onChange(v);
        }}
        className="h-7 w-[86px] rounded-[7px] bg-[var(--bg-sunken)] px-2 font-mono text-[12px] uppercase shadow-[inset_0_0_0_1px_var(--hairline)] outline-none focus:shadow-[inset_0_0_0_1px_var(--accent)]"
      />
    </div>
  );
}

export function Divider() {
  return <div className="h-px w-full bg-[var(--hairline)]" />;
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <div className="mb-1 text-[var(--text-tertiary)]">{icon}</div>
      <p className="text-[13px] font-medium text-[var(--text-secondary)]">{title}</p>
      {hint && <p className="max-w-[220px] text-[12px] leading-snug text-[var(--text-tertiary)]">{hint}</p>}
      {action}
    </div>
  );
}
